import { SharedCommonUtility } from './common.utility';
import { IParsedStandard } from '../interfaces/standard-parsing.interface';
import { ParsedStandards } from '../constants/standard-parsing';
import { IAuditStandard } from '../interfaces/audit-standard.interface';
import { $auditStandard, mapAuditDbStandardToAuditStandard } from '../constants/audit-standard';
import { IPopulatedManualFindingStandard } from '../interfaces/manual-audit-finding.interface';
import { wcagNameAndLevel } from '../constants/scanning';
import { EVALUATION_STANDARDS, EvaluationStandards } from '../constants/manual-evaluation';

export class SharedAuditStandardsUtility {
  public static filterStandards<AuditStandard extends IAuditStandard | IPopulatedManualFindingStandard>(
    auditStandards: AuditStandard[],
    nameVersionLevel: string,
  ): AuditStandard[] {
    const { standard, version, levels }: IParsedStandard = ParsedStandards[nameVersionLevel] ?? { standard: nameVersionLevel };

    const compareStandard = (value: AuditStandard): boolean =>
      mapAuditDbStandardToAuditStandard[value[$auditStandard.standard]] === standard;
    const compareVersion = (value: AuditStandard): boolean => value[$auditStandard.version] === version;
    const compareLevel = (value: AuditStandard): boolean => levels.includes(value[$auditStandard.level]);

    if (SharedCommonUtility.isNullish(version) && SharedCommonUtility.isNullishOrEmpty(levels)) {
      return auditStandards.filter(compareStandard);
    }

    if (SharedCommonUtility.isNullish(version)) {
      return auditStandards.filter((value: AuditStandard): boolean => compareStandard(value) && compareLevel(value));
    }

    return auditStandards.filter(
      (value: AuditStandard): boolean => compareStandard(value) && compareLevel(value) && compareVersion(value),
    );
  }

  public static conformanceLevelToEvaluationStandard(conformanceLevel: wcagNameAndLevel): EvaluationStandards {
    if (!EVALUATION_STANDARDS.includes(conformanceLevel as unknown as EvaluationStandards)) {
      throw new Error('Cannot convert conformance level to evaluation standard, conformance level value is likely invalid');
    }
    return EvaluationStandards[conformanceLevel];
  }

  public static inflateWcagStandard(standard: EvaluationStandards): EvaluationStandards[] {
    const standardStr: string = standard.toString();
    if (!standardStr.startsWith('WCAG')) {
      return [standard];
    }
    const prefix: string = standardStr.slice(0, standardStr.lastIndexOf('_'));
    const suffix: string = standardStr.slice(prefix.length);
    const allStandards: EvaluationStandards[] = Object.values(EvaluationStandards) as EvaluationStandards[];
    return allStandards.filter(
      (s: EvaluationStandards) => s.toString().startsWith(prefix) && s.toString().slice(prefix.length).length <= suffix.length,
    );
  }

  public static deflateWcagStandards(standards: EvaluationStandards[]): EvaluationStandards {
    const wcagOnly: EvaluationStandards[] = standards?.filter((s: EvaluationStandards) => s.toString().startsWith('WCAG'));
    if (SharedCommonUtility.isNullishOrEmpty(wcagOnly)) {
      return undefined;
    }
    const prefix: string = wcagOnly[0].toString().slice(0, wcagOnly[0].toString().lastIndexOf('_'));
    if (wcagOnly.some((s: EvaluationStandards) => !s.toString().startsWith(prefix))) {
      throw new Error(`Not all standards share the same prefix ${wcagOnly}`);
    }
    wcagOnly.sort((s1: EvaluationStandards, s2: EvaluationStandards) => s2.toString().length - s1.toString().length);
    return wcagOnly[0];
  }
}
